import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface PhonesAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  disabled: boolean;
}

export default function PhonesAccordion({
  control,
  errors,
  disabled,
  watch,
}: PhonesAccordionProps) {
  const [open, setOpen] = useState(false);
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'phones',
  });

  const menuItems = [
    { value: 'assistant', label: 'Assistant' },
    { value: 'callback', label: 'Callback' },
    { value: 'car', label: 'Car' },
    { value: 'company_main', label: 'Company Main' },
    { value: 'custom', label: 'Custom' },
    { value: 'grand_central', label: 'Google Voice' },
    { value: 'home', label: 'Home' },
    { value: 'home_fax', label: 'Home Fax' },
    { value: 'isdn', label: 'ISDN' },
    { value: 'main', label: 'Main' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'other', label: 'Other' },
    { value: 'other_fax', label: 'Other Fax' },
    { value: 'pager', label: 'Pager' },
    { value: 'radio', label: 'Radio' },
    { value: 'telex', label: 'Telex' },
    { value: 'tty_tdd', label: 'TTY TDD' },
    { value: 'work', label: 'Work' },
    { value: 'work_fax', label: 'Work Fax' },
    { value: 'work_mobile', label: 'Work Mobile' },
    { value: 'work_pager', label: 'Work Pager' },
  ];

  const phonesErrors = errors['phones'];

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (phonesErrors) {
      setOpen(true);
    }
  }, [phonesErrors]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        if (fields.length === 0) return;
        setOpen(!open);
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton
            component="span"
            disabled={fields.length === 0}
            sx={{ mr: -1 }}
            disableRipple
          >
            <ExpandMore />
          </IconButton>
        }
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: (theme) =>
                phonesErrors ? theme.palette.error.main : 'inherit',
            }}
          >
            Phones
          </Typography>
          <Tooltip title="Add" arrow>
            <span>
              <IconButton
                component="span"
                size="small"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  append({
                    type: '',
                    primary: false,
                    customType: '',
                    value: '',
                  });
                }}
              >
                <AddCircle />
              </IconButton>
            </span>
          </Tooltip>
          {fields.length > 0 ? (
            <Chip label={fields.length} color="primary" size="small" />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        {fields.map((item, index) => {
          const typeField = watch(`phones.${index}.type`);

          return (
            <Box key={item.id}>
              <Stack
                gap={1}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ my: 1.5 }}
              >
                <Stack flexDirection="row" alignItems="center" gap={2}>
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`phones.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `phones-type-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `phones-type-${index}`,
                        }}
                        name="phones"
                        select
                        disabled={disabled}
                        label="Type"
                        error={!!phonesErrors?.[index]?.type}
                        helperText={
                          phonesErrors?.[index]?.type
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 150,
                        }}
                      >
                        {menuItems.map((item) => (
                          <MenuItem value={item.value} key={item.label}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {typeField === 'custom' && (
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`phones.${index}.customType`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          inputRef={field.ref}
                          inputProps={{
                            id: `phones-customType-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `phones-customType-${index}`,
                          }}
                          disabled={disabled}
                          name="phones"
                          label="Custom Type"
                          error={!!phonesErrors?.[index]?.customType}
                          helperText={
                            phonesErrors?.[index]?.customType
                              ? 'This field is required.'
                              : ''
                          }
                          sx={{
                            minWidth: 225,
                          }}
                        />
                      )}
                    />
                  )}
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`phones.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        name="phones"
                        inputRef={field.ref}
                        error={!!phonesErrors?.[index]?.value}
                        helperText={
                          phonesErrors?.[index]?.value
                            ? 'This field is required.'
                            : ''
                        }
                        disabled={disabled}
                        autoComplete="off"
                        label="Phone Number"
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                </Stack>

                {/* actions */}
                <Stack flexDirection="row" gap={2}>
                  <Tooltip title="Move Down" arrow>
                    <span>
                      <IconButton
                        component="span"
                        disabled={index === fields.length - 1 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index + 1);
                        }}
                      >
                        <ArrowDownward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move Up" arrow>
                    <span>
                      <IconButton
                        component="span"
                        disabled={index === 0 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index - 1);
                        }}
                      >
                        <ArrowUpward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton
                        component="span"
                        size="small"
                        disabled={disabled}
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              {index !== fields.length - 1 && <Divider />}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
